import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Boxes,
  CheckCircle2,
  Copy,
  LayoutGrid,
  Plus,
  Scissors,
  Store as StoreIcon,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { MediaFormat, Store } from '@shared/schema';
import {
  calculateTotalPreview,
  type GroupFormatEntry,
  type GroupValidationError,
  type GroupValidationWarning,
  type StoreFormatGroup,
} from '@shared/store-format-groups';

type SplitMode = 'formats' | 'stores' | 'matrix';

interface OperatorTask {
  id: number;
  task_id: number;
  task_name?: string;
  name?: string;
}

interface RestructureGroupBuilderProps {
  campaignId: number | null;
  retailerId: number;
  groups: StoreFormatGroup[];
  onGroupsChange: (groups: StoreFormatGroup[]) => void;
  validationErrors?: GroupValidationError[];
  validationWarnings?: GroupValidationWarning[];
}

function generateGroupId(): string {
  return `group_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function cloneFormatEntry(entry: GroupFormatEntry): GroupFormatEntry {
  return { ...entry };
}

export function RestructureGroupBuilder({
  retailerId,
  groups,
  onGroupsChange,
  validationErrors = [],
  validationWarnings = [],
}: RestructureGroupBuilderProps) {
  const { toast } = useToast();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(groups[0]?.groupId ?? null);
  const [splitMode, setSplitMode] = useState<SplitMode>('formats');
  const [selectedFormatIds, setSelectedFormatIds] = useState<Set<number>>(new Set());
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<number>>(new Set());
  const [moveTargetGroupId, setMoveTargetGroupId] = useState<string>('__new__');
  const [newGroupName, setNewGroupName] = useState('New split group');
  const [deletedGroup, setDeletedGroup] = useState<{ group: StoreFormatGroup; index: number } | null>(null);

  const { data: stores = [], isLoading: storesLoading } = useQuery<Store[]>({
    queryKey: ['/api/retailers', retailerId, 'stores'],
    queryFn: async () => {
      const response = await apiFetch(`/api/retailers/${retailerId}/stores`);
      if (!response.ok) throw new Error('Failed to fetch stores');
      return response.json();
    },
    enabled: !!retailerId,
  });

  const { data: mediaFormats = [], isLoading: formatsLoading } = useQuery<MediaFormat[]>({
    queryKey: ['/api/media-formats', retailerId],
    queryFn: async () => {
      const response = await apiFetch(`/api/media-formats?retailerId=${retailerId}`);
      if (!response.ok) throw new Error('Failed to fetch media formats');
      return response.json();
    },
    enabled: !!retailerId,
  });

  const { data: operatorTasks = [], isLoading: operatorTasksLoading } = useQuery<OperatorTask[]>({
    queryKey: ['/api/operator-tasks'],
    queryFn: async () => {
      const response = await apiFetch('/api/operator-tasks');
      if (!response.ok) throw new Error('Failed to fetch operator tasks');
      return response.json();
    },
  });

  const selectedGroup = groups.find((group) => group.groupId === selectedGroupId) ?? groups[0] ?? null;
  const targetGroups = groups.filter((group) => group.groupId !== selectedGroup?.groupId);
  const storeNameById = useMemo(
    () => new Map(stores.map((store) => [store.id, store.name || `Store ${store.id}`])),
    [stores],
  );
  const formatNameById = useMemo(
    () => new Map(mediaFormats.map((format) => [format.id, format.name || `Format ${format.id}`])),
    [mediaFormats],
  );
  const taskNameById = useMemo(
    () => new Map(operatorTasks.map((task) => [task.task_id, task.task_name || task.name || `Task ${task.task_id}`])),
    [operatorTasks],
  );
  const totalPreview = useMemo(() => calculateTotalPreview(groups), [groups]);
  const hasEmptyGroup = groups.some((group) => group.storeIds.length === 0 || group.formatEntries.length === 0);
  const hasValidationErrors = validationErrors.length > 0;
  const hasValidationWarnings = validationWarnings.length > 0;
  const isLoading = storesLoading || formatsLoading || operatorTasksLoading;

  useEffect(() => {
    if (!selectedGroupId || !groups.some((group) => group.groupId === selectedGroupId)) {
      setSelectedGroupId(groups[0]?.groupId ?? null);
    }
  }, [groups, selectedGroupId]);

  useEffect(() => {
    if (selectedGroup) {
      setNewGroupName(`${selectedGroup.groupName || 'Group'} split`);
    }
    setSelectedFormatIds(new Set());
    setSelectedStoreIds(new Set());
    setMoveTargetGroupId('__new__');
  }, [selectedGroup?.groupId]);

  const updateGroup = (groupId: string, updater: (group: StoreFormatGroup) => StoreFormatGroup) => {
    setDeletedGroup(null);
    onGroupsChange(groups.map((group) => (group.groupId === groupId ? updater(group) : group)));
  };

  const addEmptyGroup = () => {
    const newGroup: StoreFormatGroup = {
      groupId: generateGroupId(),
      groupName: `Group ${groups.length + 1}`,
      storeIds: [],
      formatEntries: [],
      labelId: null,
    };
    setDeletedGroup(null);
    onGroupsChange([...groups, newGroup]);
    setSelectedGroupId(newGroup.groupId);
  };

  const duplicateSelectedGroup = () => {
    if (!selectedGroup) return;
    const newGroup: StoreFormatGroup = {
      ...selectedGroup,
      groupId: generateGroupId(),
      groupName: `${selectedGroup.groupName || 'Group'} copy`,
      storeIds: [...selectedGroup.storeIds],
      formatEntries: selectedGroup.formatEntries.map(cloneFormatEntry),
      operatorAssignments: selectedGroup.operatorAssignments?.map((assignment) => ({ ...assignment })),
      storeCoverageOverrides: selectedGroup.storeCoverageOverrides
        ? { ...selectedGroup.storeCoverageOverrides }
        : undefined,
    };
    setDeletedGroup(null);
    onGroupsChange([...groups, newGroup]);
    setSelectedGroupId(newGroup.groupId);
  };

  const deleteSelectedGroup = () => {
    if (!selectedGroup) return;
    const deletedIndex = groups.findIndex((group) => group.groupId === selectedGroup.groupId);
    const nextGroups = groups.filter((group) => group.groupId !== selectedGroup.groupId);
    setDeletedGroup({ group: selectedGroup, index: deletedIndex });
    onGroupsChange(nextGroups);
    setSelectedGroupId(nextGroups[0]?.groupId ?? null);
  };

  const undoDeleteGroup = () => {
    if (!deletedGroup) return;
    const insertIndex = Math.max(0, Math.min(deletedGroup.index, groups.length));
    const nextGroups = [...groups.slice(0, insertIndex), deletedGroup.group, ...groups.slice(insertIndex)];
    onGroupsChange(nextGroups);
    setSelectedGroupId(deletedGroup.group.groupId);
    setDeletedGroup(null);
  };

  const toggleFormat = (formatId: number) => {
    setSelectedFormatIds((current) => {
      const next = new Set(current);
      next.has(formatId) ? next.delete(formatId) : next.add(formatId);
      return next;
    });
  };

  const toggleStore = (storeId: number) => {
    setSelectedStoreIds((current) => {
      const next = new Set(current);
      next.has(storeId) ? next.delete(storeId) : next.add(storeId);
      return next;
    });
  };

  const moveSelectedFormats = () => {
    if (!selectedGroup) return;
    const movingEntries = selectedGroup.formatEntries
      .filter((entry) => selectedFormatIds.has(entry.mediaFormatId))
      .map(cloneFormatEntry);

    if (movingEntries.length === 0) return;
    if (movingEntries.length >= selectedGroup.formatEntries.length) {
      toast({
        title: 'Cannot move all formats',
        description: 'Leave at least one format in the source group, or delete the group separately.',
        variant: 'destructive',
      });
      return;
    }

    const movingLimitedFormat = movingEntries.find((entry) => {
      const format = mediaFormats.find((candidate) => candidate.id === entry.mediaFormatId);
      return entry.coveragePercent != null || Boolean(format && (format as any).capacityType === 'limited');
    });
    if (movingLimitedFormat) {
      toast({
        title: 'Cannot move limited-capacity format',
        description: 'POPAd and limited-capacity formats need their coverage rules preserved. Use advanced fine-tuning for those groups.',
        variant: 'destructive',
      });
      return;
    }

    const movingFormatIds = new Set(movingEntries.map((entry) => entry.mediaFormatId));
    const sourceGroup: StoreFormatGroup = {
      ...selectedGroup,
      formatEntries: selectedGroup.formatEntries.filter((entry) => !movingFormatIds.has(entry.mediaFormatId)),
      operatorAssignments: selectedGroup.operatorAssignments?.filter(
        (assignment) => !movingFormatIds.has(assignment.mediaFormatId),
      ),
    };
    const assignmentsToMove = selectedGroup.operatorAssignments?.filter((assignment) =>
      movingFormatIds.has(assignment.mediaFormatId),
    ) ?? [];

    if (moveTargetGroupId !== '__new__') {
      const targetGroup = groups.find((group) => group.groupId === moveTargetGroupId);
      if (!targetGroup) return;
      const targetFormatIds = new Set(targetGroup.formatEntries.map((entry) => entry.mediaFormatId));
      const duplicate = movingEntries.find((entry) => targetFormatIds.has(entry.mediaFormatId));
      if (duplicate) {
        toast({
          title: 'Format already exists in target group',
          description: `${formatNameById.get(duplicate.mediaFormatId) ?? `Format ${duplicate.mediaFormatId}`} is already in "${targetGroup.groupName}".`,
          variant: 'destructive',
        });
        return;
      }

      const targetStoreIds = new Set(targetGroup.storeIds);
      const assignmentsForTarget = assignmentsToMove.filter((assignment) => targetStoreIds.has(assignment.storeId));
      const nextTarget: StoreFormatGroup = {
        ...targetGroup,
        formatEntries: [...targetGroup.formatEntries, ...movingEntries],
        operatorAssignments: normalizeAssignmentsForGroup(
          { ...targetGroup, formatEntries: [...targetGroup.formatEntries, ...movingEntries] },
          mergeAssignments(targetGroup.operatorAssignments ?? [], assignmentsForTarget),
        ),
      };

      setDeletedGroup(null);
      onGroupsChange(groups.map((group) => {
        if (group.groupId === sourceGroup.groupId) return {
          ...sourceGroup,
          operatorAssignments: normalizeAssignmentsForGroup(sourceGroup, sourceGroup.operatorAssignments ?? []),
        };
        if (group.groupId === nextTarget.groupId) return nextTarget;
        return group;
      }));
      setSelectedGroupId(nextTarget.groupId);
      setSelectedFormatIds(new Set());
      return;
    }

    const newGroup: StoreFormatGroup = {
      ...selectedGroup,
      groupId: generateGroupId(),
      groupName: newGroupName.trim() || `${selectedGroup.groupName || 'Group'} split`,
      storeIds: [...selectedGroup.storeIds],
      formatEntries: movingEntries,
      operatorAssignments: assignmentsToMove.map((assignment) => ({ ...assignment })),
      storeCoverageOverrides: selectedGroup.storeCoverageOverrides
        ? { ...selectedGroup.storeCoverageOverrides }
        : undefined,
    };

    setDeletedGroup(null);
    onGroupsChange(groups.map((group) => (group.groupId === sourceGroup.groupId ? sourceGroup : group)).concat(newGroup));
    setSelectedGroupId(newGroup.groupId);
    setSelectedFormatIds(new Set());
  };

  const moveSelectedStores = () => {
    if (!selectedGroup) return;
    const movingStoreIds = selectedGroup.storeIds.filter((storeId) => selectedStoreIds.has(storeId));
    if (movingStoreIds.length === 0) return;
    if (movingStoreIds.length >= selectedGroup.storeIds.length) {
      toast({
        title: 'Cannot move all stores',
        description: 'Leave at least one store in the source group, or delete the group separately.',
        variant: 'destructive',
      });
      return;
    }

    const movingStoreSet = new Set(movingStoreIds);
    const sourceCoverageOverrides = selectedGroup.storeCoverageOverrides ?? {};
    const sourceGroup: StoreFormatGroup = {
      ...selectedGroup,
      storeIds: selectedGroup.storeIds.filter((storeId) => !movingStoreSet.has(storeId)),
      operatorAssignments: selectedGroup.operatorAssignments?.filter(
        (assignment) => !movingStoreSet.has(assignment.storeId),
      ),
      storeCoverageOverrides: Object.fromEntries(
        Object.entries(sourceCoverageOverrides).filter(([storeId]) => !movingStoreSet.has(Number(storeId))),
      ),
    };
    const assignmentsToMove = selectedGroup.operatorAssignments?.filter((assignment) =>
      movingStoreSet.has(assignment.storeId),
    ) ?? [];
    const coverageToMove = Object.fromEntries(
      Object.entries(sourceCoverageOverrides).filter(([storeId]) => movingStoreSet.has(Number(storeId))),
    );

    if (moveTargetGroupId !== '__new__') {
      const targetGroup = groups.find((group) => group.groupId === moveTargetGroupId);
      if (!targetGroup) return;
      const nextTargetStoreIds = Array.from(new Set([...targetGroup.storeIds, ...movingStoreIds]));
      const nextTarget: StoreFormatGroup = {
        ...targetGroup,
        storeIds: nextTargetStoreIds,
        operatorAssignments: normalizeAssignmentsForGroup(
          { ...targetGroup, storeIds: nextTargetStoreIds },
          mergeAssignments(targetGroup.operatorAssignments ?? [], assignmentsToMove),
        ),
        storeCoverageOverrides: {
          ...(targetGroup.storeCoverageOverrides ?? {}),
          ...coverageToMove,
        },
      };

      setDeletedGroup(null);
      onGroupsChange(groups.map((group) => {
        if (group.groupId === sourceGroup.groupId) return {
          ...sourceGroup,
          operatorAssignments: normalizeAssignmentsForGroup(sourceGroup, sourceGroup.operatorAssignments ?? []),
        };
        if (group.groupId === nextTarget.groupId) return nextTarget;
        return group;
      }));
      setSelectedGroupId(nextTarget.groupId);
      setSelectedStoreIds(new Set());
      return;
    }

    const newGroup: StoreFormatGroup = {
      ...selectedGroup,
      groupId: generateGroupId(),
      groupName: newGroupName.trim() || `${selectedGroup.groupName || 'Group'} split`,
      storeIds: movingStoreIds,
      formatEntries: selectedGroup.formatEntries.map(cloneFormatEntry),
      operatorAssignments: assignmentsToMove.map((assignment) => ({ ...assignment })),
      storeCoverageOverrides: coverageToMove,
    };

    setDeletedGroup(null);
    onGroupsChange(groups.map((group) => (group.groupId === sourceGroup.groupId ? sourceGroup : group)).concat(newGroup));
    setSelectedGroupId(newGroup.groupId);
    setSelectedStoreIds(new Set());
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)_320px]">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Groups</CardTitle>
          <CardDescription>Campaign structure at a glance.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {groups.map((group) => (
            <button
              key={group.groupId}
              type="button"
              onClick={() => setSelectedGroupId(group.groupId)}
              className={cn(
                'w-full rounded-lg border bg-white p-3 text-left transition hover:border-primary/60 hover:shadow-sm',
                selectedGroup?.groupId === group.groupId && 'border-primary ring-2 ring-primary/15',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate-950">{group.groupName || 'Unnamed group'}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {group.operatorTaskId ? taskNameById.get(group.operatorTaskId) ?? `Task ${group.operatorTaskId}` : 'No task selected'}
                  </div>
                </div>
                <Badge variant={group.storeIds.length === 0 || group.formatEntries.length === 0 ? 'warning' : 'secondary'}>
                  {group.formatEntries.length} formats
                </Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                <span className="rounded bg-slate-50 px-2 py-1">{group.storeIds.length} stores</span>
                <span className="rounded bg-slate-50 px-2 py-1">
                  {group.formatEntries.reduce((sum, entry) => sum + entry.quantity * group.storeIds.length, 0)} qty
                </span>
              </div>
            </button>
          ))}

          <Button variant="outline" className="w-full justify-start" onClick={addEmptyGroup}>
            <Plus className="h-4 w-4" />
            Add empty group
          </Button>
        </CardContent>
      </Card>

      <main className="space-y-5">
        {deletedGroup && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span>
              Deleted <strong>{deletedGroup.group.groupName || 'Unnamed group'}</strong>. This is only applied if you save the restructure.
            </span>
            <Button variant="outline" size="sm" onClick={undoDeleteGroup}>
              Undo delete
            </Button>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-72 flex-1">
                <Label htmlFor="group-name">Group name</Label>
                <Input
                  id="group-name"
                  value={selectedGroup?.groupName ?? ''}
                  onChange={(event) => {
                    if (!selectedGroup) return;
                    updateGroup(selectedGroup.groupId, (group) => ({ ...group, groupName: event.target.value }));
                  }}
                  placeholder="Group name"
                />
                <p className="mt-2 text-sm text-slate-500">
                  {selectedGroup
                    ? `${selectedGroup.storeIds.length} stores, ${selectedGroup.formatEntries.length} formats`
                    : 'Select or add a group to begin.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={duplicateSelectedGroup} disabled={!selectedGroup}>
                  <Copy className="h-4 w-4" />
                  Duplicate
                </Button>
                <Button variant="outline" size="sm" onClick={deleteSelectedGroup} disabled={!selectedGroup || groups.length <= 1}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedGroup && (
              <>
                <div className="grid gap-3 md:grid-cols-3">
                  <ModeButton
                    active={splitMode === 'formats'}
                    icon={<Boxes className="h-4 w-4" />}
                    title="Move Formats"
                    description="Move chosen formats into a new or existing group."
                    onClick={() => setSplitMode('formats')}
                  />
                  <ModeButton
                    active={splitMode === 'stores'}
                    icon={<StoreIcon className="h-4 w-4" />}
                    title="Move Stores"
                    description="Move chosen stores into a new or existing group."
                    onClick={() => setSplitMode('stores')}
                  />
                  <ModeButton
                    active={splitMode === 'matrix'}
                    icon={<LayoutGrid className="h-4 w-4" />}
                    title="Matrix View"
                    description="See store x format coverage at a glance."
                    onClick={() => setSplitMode('matrix')}
                  />
                </div>

                <div className="rounded-xl border bg-white p-4">
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-slate-950">
                        {splitMode === 'formats' && 'Choose formats to move'}
                        {splitMode === 'stores' && 'Choose stores to move'}
                        {splitMode === 'matrix' && 'Coverage matrix'}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {splitMode === 'matrix'
                          ? 'Every checked cell is generated by this group: each selected store receives each selected format.'
                          : 'The selected items leave the source group and move to the target group.'}
                      </p>
                    </div>
                    {splitMode !== 'matrix' && (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1">
                          <Label>Move to</Label>
                          <Select value={moveTargetGroupId} onValueChange={setMoveTargetGroupId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose target" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__new__">New group</SelectItem>
                              {targetGroups.map((group) => (
                                <SelectItem key={group.groupId} value={group.groupId}>
                                  {group.groupName || 'Unnamed group'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {moveTargetGroupId === '__new__' && (
                          <div className="space-y-1">
                            <Label htmlFor="new-group-name">New group name</Label>
                            <Input
                              id="new-group-name"
                              value={newGroupName}
                              onChange={(event) => setNewGroupName(event.target.value)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {splitMode === 'formats' && (
                    <div className="space-y-2">
                      {selectedGroup.formatEntries.map((entry) => (
                        <label
                          key={entry.mediaFormatId}
                          className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-slate-50"
                        >
                          <span className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedFormatIds.has(entry.mediaFormatId)}
                              onChange={() => toggleFormat(entry.mediaFormatId)}
                              className="h-4 w-4"
                            />
                            <span>
                              <span className="block font-medium">
                                {formatNameById.get(entry.mediaFormatId) ?? `Format ${entry.mediaFormatId}`}
                              </span>
                              <span className="text-xs text-slate-500">
                                Quantity {entry.quantity}
                                {entry.coveragePercent ? `, ${entry.coveragePercent}% coverage` : ''}
                              </span>
                            </span>
                          </span>
                          <Badge variant="outline">format</Badge>
                        </label>
                      ))}
                      {selectedGroup.formatEntries.length === 0 && (
                        <EmptyState message="This group has no formats. Add formats in the existing advanced editor before saving." />
                      )}
                    </div>
                  )}

                  {splitMode === 'stores' && (
                    <div className="grid gap-2 md:grid-cols-2">
                      {selectedGroup.storeIds.map((storeId) => (
                        <label
                          key={storeId}
                          className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStoreIds.has(storeId)}
                            onChange={() => toggleStore(storeId)}
                            className="h-4 w-4"
                          />
                          <span className="font-medium">{storeNameById.get(storeId) ?? `Store ${storeId}`}</span>
                        </label>
                      ))}
                      {selectedGroup.storeIds.length === 0 && (
                        <EmptyState message="This group has no stores. Add stores in the existing advanced editor before saving." />
                      )}
                    </div>
                  )}

                  {splitMode === 'matrix' && (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] border-collapse text-sm">
                        <thead>
                          <tr>
                            <th className="border-b p-3 text-left text-slate-500">Store</th>
                            {selectedGroup.formatEntries.map((entry) => (
                              <th key={entry.mediaFormatId} className="border-b p-3 text-left text-slate-500">
                                {formatNameById.get(entry.mediaFormatId) ?? `Format ${entry.mediaFormatId}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedGroup.storeIds.map((storeId) => (
                            <tr key={storeId}>
                              <td className="border-b p-3 font-medium">{storeNameById.get(storeId) ?? `Store ${storeId}`}</td>
                              {selectedGroup.formatEntries.map((entry) => (
                                <td key={entry.mediaFormatId} className="border-b p-3">
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {splitMode !== 'matrix' && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={splitMode === 'formats' ? moveSelectedFormats : moveSelectedStores}
                        disabled={splitMode === 'formats' ? selectedFormatIds.size === 0 : selectedStoreIds.size === 0}
                      >
                        <Scissors className="h-4 w-4" />
                        Move Selected
                      </Button>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border bg-white p-4">
                  <h2 className="mb-3 font-semibold text-slate-950">Group setup</h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label>Operator task</Label>
                      <Select
                        value={selectedGroup.operatorTaskId ? String(selectedGroup.operatorTaskId) : undefined}
                        onValueChange={(value) => {
                          const parsed = Number(value);
                          updateGroup(selectedGroup.groupId, (group) => ({
                            ...group,
                            operatorTaskId: Number.isFinite(parsed) ? parsed : null,
                          }));
                        }}
                        disabled={isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isLoading ? 'Loading tasks...' : 'Select operator task'} />
                        </SelectTrigger>
                        <SelectContent>
                          {operatorTasks.map((task) => (
                            <SelectItem key={task.id} value={String(task.task_id)}>
                              {task.task_name || task.name || `Task ${task.task_id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                      Use the move tools above for structure changes. Quantity, POPAd coverage, label, and instruction fine-tuning can remain in the advanced editor until the full Restructure surface covers those fields.
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Impact Preview</CardTitle>
          <CardDescription>Always visible before save.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Groups" value={totalPreview.groupCount} />
            <Metric label="Stores" value={totalPreview.totalStores} />
            <Metric label="Formats" value={totalPreview.totalFormats} />
            <Metric label="Units" value={totalPreview.totalUnits} />
          </div>

          <div className={cn(
            'rounded-lg border p-3 text-sm',
            hasValidationErrors || hasEmptyGroup
              ? 'bg-red-50 text-red-800'
              : hasValidationWarnings
                ? 'bg-amber-50 text-amber-800'
                : 'bg-emerald-50 text-emerald-800',
          )}>
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="h-4 w-4" />
              {hasValidationErrors || hasEmptyGroup
                ? 'Needs attention'
                : hasValidationWarnings
                  ? 'Warnings to review'
                  : 'Valid basic structure'}
            </div>
            <p className="mt-1">
              {hasValidationErrors
                ? 'Server validation found issues. Fix these before previewing.'
                : hasEmptyGroup
                  ? 'One or more groups has no stores or no formats. Fix before saving.'
                  : hasValidationWarnings
                    ? 'The structure can continue, but review the warnings first.'
                    : 'No empty groups. Server validation still runs before preview.'}
            </p>
          </div>

          {(hasValidationErrors || hasValidationWarnings) && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Validation Results</h3>
              {validationErrors.slice(0, 4).map((error, index) => (
                <ValidationMessage
                  key={`error-${index}`}
                  tone="error"
                  message={error.message}
                  groupName={groups.find((group) => group.groupId === error.groupId)?.groupName}
                  onSelectGroup={error.groupId ? () => setSelectedGroupId(error.groupId ?? null) : undefined}
                />
              ))}
              {validationWarnings.slice(0, 3).map((warning, index) => (
                <ValidationMessage
                  key={`warning-${index}`}
                  tone="warning"
                  message={warning.message}
                  groupName={groups.find((group) => group.groupId === warning.groupId)?.groupName}
                  onSelectGroup={warning.groupId ? () => setSelectedGroupId(warning.groupId ?? null) : undefined}
                />
              ))}
            </div>
          )}

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Save Summary</h3>
            {groups.map((group) => (
              <div key={group.groupId} className="rounded-lg border bg-white p-3 text-sm">
                <div className="font-medium">{group.groupName || 'Unnamed group'}</div>
                <div className="mt-1 text-slate-500">
                  {group.storeIds.length} stores x {group.formatEntries.length} formats
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function mergeAssignments(
  existing: NonNullable<StoreFormatGroup['operatorAssignments']>,
  incoming: NonNullable<StoreFormatGroup['operatorAssignments']>,
) {
  const keys = new Set(existing.map((assignment) => `${assignment.storeId}|${assignment.mediaFormatId}`));
  const merged = [...existing.map((assignment) => ({ ...assignment }))];
  for (const assignment of incoming) {
    const key = `${assignment.storeId}|${assignment.mediaFormatId}`;
    if (keys.has(key)) continue;
    keys.add(key);
    merged.push({ ...assignment });
  }
  return merged;
}

function normalizeAssignmentsForGroup(
  group: StoreFormatGroup,
  assignments: NonNullable<StoreFormatGroup['operatorAssignments']>,
) {
  const storeIds = new Set(group.storeIds);
  const formatIds = new Set(group.formatEntries.map((entry) => entry.mediaFormatId));
  return assignments
    .filter((assignment) => storeIds.has(assignment.storeId) && formatIds.has(assignment.mediaFormatId))
    .map((assignment) => ({ ...assignment }));
}

function ModeButton({
  active,
  icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-white p-3 text-left transition hover:border-primary/60',
        active && 'border-primary bg-primary/5 ring-2 ring-primary/10',
      )}
    >
      <div className="flex items-center gap-2 font-semibold text-slate-950">
        {icon}
        {title}
      </div>
      <p className="mt-1 text-xs text-slate-500">{description}</p>
    </button>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="text-2xl font-bold text-slate-950">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed bg-slate-50 p-4 text-sm text-slate-500">
      {message}
    </div>
  );
}

function ValidationMessage({
  tone,
  message,
  groupName,
  onSelectGroup,
}: {
  tone: 'error' | 'warning';
  message: string;
  groupName?: string;
  onSelectGroup?: () => void;
}) {
  return (
    <div className={cn(
      'rounded-lg border p-3 text-sm',
      tone === 'error'
        ? 'border-red-200 bg-red-50 text-red-800'
        : 'border-amber-200 bg-amber-50 text-amber-800',
    )}>
      {groupName && <div className="mb-1 font-semibold">{groupName}</div>}
      <div>{message}</div>
      {onSelectGroup && (
        <Button variant="outline" size="sm" className="mt-2" onClick={onSelectGroup}>
          View group
        </Button>
      )}
    </div>
  );
}
